/*
  <bold-gallery> — custom gallery controller for the Bold product hero.

  Responsibilities (visual only — it never touches variant/cart mechanics):
  - One large hero media + a horizontal filmstrip of thumbnails with ‹ › arrows.
  - Clicking a thumb sets the hero. Active thumb is highlighted + auto-centered.
  - Clicking the hero (or "Mărește") opens a minimal accessible lightbox with
    focus trap, Esc, and arrow-key navigation.
  - VARIANT-AWARE: subscribes to PUB_SUB_EVENTS.variantChange and, when the new
    variant has a featured_media, makes that media the active hero. There is NO
    parallel variant state here — this element only ever REACTS to variantChange
    (fired by product-info.js) and to user thumb/arrow/lightbox interaction.

  Guarded custom element (no double-define). Keyed to its own section via the
  [data-bold-hero] attribute, so multiple instances on a page never interfere.
  rAF is used for scroll-driven centering; prefers-reduced-motion is respected
  (instant scroll + no smooth behavior). Defer-loaded.
*/
if (!customElements.get('bold-gallery')) {
  customElements.define(
    'bold-gallery',
    class BoldGallery extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.boldHero || '';
        this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        // Core nodes
        this.heroMedia = this.querySelector('[data-bold-hero-media]');
        this.strip = this.querySelector('[data-bold-strip]');
        this.prevBtn = this.querySelector('[data-bold-prev]');
        this.nextBtn = this.querySelector('[data-bold-next]');
        this.counter = this.querySelector('[data-bold-counter]');
        this.thumbs = Array.from(this.querySelectorAll('[data-bold-thumb]'));

        // Lightbox nodes
        this.lightbox = this.querySelector('[data-bold-lightbox]');
        this.lbImage = this.querySelector('[data-bold-lb-image]');
        this.lbCounter = this.querySelector('[data-bold-lb-counter]');
        this.lbStrip = this.querySelector('[data-bold-lb-strip]');
        this.lbThumbs = Array.from(this.querySelectorAll('[data-bold-lb-thumb]'));

        if (!this.heroMedia || !this.thumbs.length) return;

        this.activeIndex = this.thumbs.findIndex((t) => t.getAttribute('aria-current') === 'true');
        if (this.activeIndex < 0) this.activeIndex = 0;

        this.bindEvents();
        this.subscribeVariant();
        this.setActive(this.activeIndex, { scroll: false });
      }

      disconnectedCallback() {
        if (this.unsubscribeVariant) this.unsubscribeVariant();
        document.removeEventListener('keydown', this.onKeydown);
      }

      bindEvents() {
        this.thumbs.forEach((thumb, i) => {
          thumb.addEventListener('click', () => this.setActive(i, { scroll: true }));
        });

        this.prevBtn?.addEventListener('click', () => this.step(-1));
        this.nextBtn?.addEventListener('click', () => this.step(1));

        // Open lightbox from the hero or any explicit zoom trigger.
        this.querySelectorAll('[data-bold-zoom]').forEach((el) => {
          el.addEventListener('click', () => this.openLightbox());
        });

        // Lightbox controls
        this.querySelector('[data-bold-lb-close]')?.addEventListener('click', () => this.closeLightbox());
        this.querySelector('[data-bold-lb-prev]')?.addEventListener('click', () => this.step(-1));
        this.querySelector('[data-bold-lb-next]')?.addEventListener('click', () => this.step(1));
        this.lightbox?.addEventListener('click', (e) => {
          if (e.target === this.lightbox) this.closeLightbox();
        });
        this.lbThumbs.forEach((thumb, i) => {
          thumb.addEventListener('click', () => this.setActive(i, { scroll: true }));
        });

        this.onKeydown = this.onKeydown.bind(this);
      }

      subscribeVariant() {
        if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
        this.unsubscribeVariant = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          const data = event && event.data;
          if (!data || data.sectionId !== this.sectionId) return;
          const mediaId = data.variant && data.variant.featured_media && data.variant.featured_media.id;
          if (mediaId == null) return;
          const idx = this.thumbs.findIndex((t) => String(t.dataset.mediaId) === String(mediaId));
          if (idx >= 0) this.setActive(idx, { scroll: true });
        });
      }

      step(delta) {
        const next = (this.activeIndex + delta + this.thumbs.length) % this.thumbs.length;
        this.setActive(next, { scroll: true });
      }

      setActive(index, opts) {
        const options = opts || {};
        this.activeIndex = index;
        const thumb = this.thumbs[index];
        if (!thumb) return;

        // Swap hero media from the thumb's stored source.
        this.renderHero(thumb);

        // Update thumb highlight state (both strip + lightbox).
        this.thumbs.forEach((t, i) => {
          const on = i === index;
          t.setAttribute('aria-current', on ? 'true' : 'false');
          t.classList.toggle('is-active', on);
        });
        this.lbThumbs.forEach((t, i) => {
          const on = i === index;
          t.setAttribute('aria-current', on ? 'true' : 'false');
          t.classList.toggle('is-active', on);
        });

        // Counter "n / total"
        const label = `${index + 1} / ${this.thumbs.length}`;
        if (this.counter) this.counter.textContent = label;
        if (this.lbCounter) this.lbCounter.textContent = label;

        // Lightbox large image
        if (this.lbImage) {
          this.lbImage.src = thumb.dataset.full || thumb.dataset.src || '';
          this.lbImage.alt = thumb.dataset.alt || '';
        }

        if (options.scroll !== false) {
          this.centerThumb(thumb);
          this.centerThumb(this.lbThumbs[index]);
        }
      }

      renderHero(thumb) {
        const isVideo = thumb.dataset.mediaType === 'video' || thumb.dataset.mediaType === 'external_video';
        const src = thumb.dataset.src || '';
        const alt = thumb.dataset.alt || '';
        const srcset = thumb.dataset.srcset || '';

        // Image hero (videos fall back to their poster image inside the hero; the
        // lightbox plays nothing custom — playback stays with the theme modal).
        let img = this.heroMedia.querySelector('img');
        if (!img) {
          img = document.createElement('img');
          this.heroMedia.appendChild(img);
        }
        if (srcset) img.srcset = srcset;
        img.src = src;
        img.alt = alt;
        img.loading = 'eager';
        this.heroMedia.dataset.mediaType = thumb.dataset.mediaType || 'image';
        this.heroMedia.classList.toggle('bold-hero__media--video', isVideo);
      }

      centerThumb(thumb) {
        if (!thumb || !thumb.parentElement) return;
        const strip = thumb.parentElement;
        const run = () => {
          const target = thumb.offsetLeft - strip.clientWidth / 2 + thumb.clientWidth / 2;
          strip.scrollTo({ left: target, behavior: this.reduced ? 'auto' : 'smooth' });
        };
        if (this.reduced) {
          run();
        } else {
          requestAnimationFrame(run);
        }
      }

      /* ---------------- Lightbox (accessible) ---------------- */

      openLightbox() {
        if (!this.lightbox) return;
        this.lastFocused = document.activeElement;
        this.lightbox.hidden = false;
        // Force a reflow so the open transition runs.
        // eslint-disable-next-line no-unused-expressions
        this.lightbox.offsetHeight;
        this.lightbox.classList.add('is-open');
        document.documentElement.style.overflow = 'hidden';
        document.addEventListener('keydown', this.onKeydown);
        this.setActive(this.activeIndex, { scroll: true });
        const closeBtn = this.lightbox.querySelector('[data-bold-lb-close]');
        if (closeBtn) closeBtn.focus();
      }

      closeLightbox() {
        if (!this.lightbox) return;
        this.lightbox.classList.remove('is-open');
        document.documentElement.style.overflow = '';
        document.removeEventListener('keydown', this.onKeydown);
        const finish = (e) => {
          // Ignore bubbled transitions from children — only the lightbox's own
          // opacity fade should hide it.
          if (e && e.target !== this.lightbox) return;
          this.lightbox.hidden = true;
          this.lightbox.removeEventListener('transitionend', finish);
        };
        if (this.reduced) {
          finish();
        } else {
          this.lightbox.addEventListener('transitionend', finish);
        }
        if (this.lastFocused && typeof this.lastFocused.focus === 'function') {
          this.lastFocused.focus();
        }
      }

      onKeydown(e) {
        if (this.lightbox.hidden) return;
        if (e.key === 'Escape') {
          e.preventDefault();
          this.closeLightbox();
        } else if (e.key === 'ArrowRight') {
          e.preventDefault();
          this.step(1);
        } else if (e.key === 'ArrowLeft') {
          e.preventDefault();
          this.step(-1);
        } else if (e.key === 'Tab') {
          this.trapFocus(e);
        }
      }

      trapFocus(e) {
        const focusable = this.lightbox.querySelectorAll(
          'button:not([disabled]), [href], input, [tabindex]:not([tabindex="-1"])'
        );
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }
  );
}
